/**
 * Modal dialog.
 *
 * Uses the native <dialog> element so focus trapping, Escape-to-close and the
 * top-layer stacking come from the platform rather than hand-rolled JavaScript.
 */

import { useEffect, useRef, useCallback } from 'react';
import { Button } from './Button.jsx';
import styles from './Modal.module.css';

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  dismissible = true,
  className = '',
}) {
  const dialogRef = useRef(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  // The native `cancel` event fires on Escape. Honour `dismissible` here so a
  // required decision cannot be skipped, without breaking keyboard behaviour.
  const handleCancel = useCallback(
    (event) => {
      event.preventDefault();
      if (dismissible) onClose?.();
    },
    [dismissible, onClose],
  );

  /** Clicking the backdrop (outside the panel) closes a dismissible dialog. */
  const handleClick = useCallback(
    (event) => {
      if (!dismissible) return;
      if (event.target === dialogRef.current) onClose?.();
    },
    [dismissible, onClose],
  );

  return (
    <dialog
      ref={dialogRef}
      className={`${styles.dialog} ${styles[size]} ${className}`}
      onCancel={handleCancel}
      onClick={handleClick}
      aria-labelledby={title ? 'modal-title' : undefined}
      aria-describedby={description ? 'modal-description' : undefined}
    >
      <div className={styles.panel}>
        {(title || dismissible) && (
          <header className={styles.header}>
            <div>
              {title && (
                <h2 className={styles.title} id="modal-title">
                  {title}
                </h2>
              )}
              {description && (
                <p className={styles.description} id="modal-description">
                  {description}
                </p>
              )}
            </div>

            {dismissible && (
              <button
                type="button"
                className={styles.close}
                onClick={onClose}
                aria-label="Close dialog"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path
                    d="M5 5l10 10M15 5L5 15"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            )}
          </header>
        )}

        <div className={styles.body}>{children}</div>

        {footer && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </dialog>
  );
}

/** Confirmation dialog for destructive or irreversible actions. */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'primary',
  loading = false,
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p>{message}</p>
    </Modal>
  );
}
